import { Cluster, ClusterStats, Renderer } from "@googlemaps/markerclusterer";
import { encodeNonAsciiHTML } from "entities";
import React from "react";
import { createRoot } from "react-dom/client";

import { LocationFinderContextProps } from "./types";

import { AppletWrapper } from "~/Atom/AppletWrapper";
import { LocationCard } from "~/Atom/LocationCard";
import { BRAND_BOOK_COLORS } from "~/Constant/Theme/configs/equipmentshare";
import { TRADE_GOTHIC_NEXT_FONT_FAMILY } from "~/Constant/Theme/constants";
import { Components } from "~/OmnichannelApi/openapi";
import { isTruthy } from "~/utils";

const div = document.createElement("div");
const root = createRoot(div);

export const createMarkerFn = (
  ctx: Pick<
    LocationFinderContextProps,
    | "mapsLib"
    | "markerLib"
    | "mountedMap"
    | "setActiveMarkerSlug"
    | "setActiveMarkerInfoWindow"
  >
) => {
  // these should always be set before calling fn, but makes typescript happy
  if (!ctx.mapsLib || !ctx.markerLib) {
    throw new Error(
      "createMarkerFn was called before the google libraries were imported!"
    );
  }

  const infoWindow = new ctx.mapsLib.InfoWindow({
    content: "",
    disableAutoPan: false,
    headerDisabled: true,
    maxWidth: 380,
  });

  return (
    loc: Components.Schemas.LocationFinderLocation
  ): google.maps.marker.AdvancedMarkerElement => {
    // these should always be set before calling fn, but makes typescript happy
    if (!ctx.mapsLib || !ctx.markerLib) {
      throw new Error(
        "createMarkerFn was called before the google libraries were imported!"
      );
    }

    const marker = new ctx.markerLib.AdvancedMarkerElement({
      title: loc.name,
      content: createMarkerElement(loc),
      position: {
        lat: loc.geo.latitude,
        lng: loc.geo.longitude,
      },
    });

    // https://developers.google.com/maps/documentation/javascript/events
    marker.addListener("click", () => {
      infoWindow.setValues({ activeMarkerSlug: loc.slug });
      ctx.setActiveMarkerInfoWindow(infoWindow);
      ctx.setActiveMarkerSlug(loc.slug);

      root.render(
        <AppletWrapper appletName="location-finder" isComponentWrapper={true}>
          <LocationCard location={loc} popup={true} />
        </AppletWrapper>
      );

      infoWindow.setContent(div);
      infoWindow.open(ctx.mountedMap, marker);

      ctx.mountedMap?.addListener("click", () => {
        ctx.setActiveMarkerSlug(undefined);
        infoWindow.close();
      });
    });

    return marker;
  };
};
export const createMarkerElement = (
  loc: Components.Schemas.LocationFinderLocation
): HTMLElement => {
  const parser = new DOMParser();

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" class="marker fadeInFast" data-location-slug="${encodeNonAsciiHTML(
    loc.slug
  )}" width="36" height="36" viewBox="0 0 36 36" fill="none" aria-label="${encodeNonAsciiHTML(
    loc.name
  )}">
    <g clip-path="url(#clip0_889_1167)">
      <path
        d="M27.546 26.046L18 35.592L8.45401 26.046C6.56601 24.158 5.28028 21.7525 4.75939 19.1338C4.23851 16.515 4.50586 13.8006 5.52765 11.3339C6.54944 8.86706 8.27977 6.75866 10.4998 5.27527C12.7199 3.79188 15.33 3.00012 18 3.00012C20.67 3.00012 23.2801 3.79188 25.5002 5.27527C27.7202 6.75866 29.4506 8.86706 30.4724 11.3339C31.4942 13.8006 31.7615 16.515 31.2406 19.1338C30.7197 21.7525 29.434 24.158 27.546 26.046ZM18 19.5C18.7957 19.5 19.5587 19.1839 20.1213 18.6213C20.6839 18.0587 21 17.2957 21 16.5C21 15.7044 20.6839 14.9413 20.1213 14.3787C19.5587 13.8161 18.7957 13.5 18 13.5C17.2044 13.5 16.4413 13.8161 15.8787 14.3787C15.3161 14.9413 15 15.7044 15 16.5C15 17.2957 15.3161 18.0587 15.8787 18.6213C16.4413 19.1839 17.2044 19.5 18 19.5Z"
        fill="${BRAND_BOOK_COLORS.BLACK_6C}"
      ></path>
    </g>
    <defs>
      <clipPath id="clip0_889_1167">
        <rect width="36" height="36" fill="white"></rect>
      </clipPath>
    </defs>
  </svg>`;

  return parser.parseFromString(svg, "image/svg+xml").documentElement;
};

export class CustomRenderer implements Renderer {
  render(
    { count, position, markers }: Cluster,
    stats: ClusterStats,
    map: google.maps.Map
  ) {
    const parser = new DOMParser();

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" class="marker-clusterer" fill="rgb(36, 36, 45)" viewBox="0 0 240 240" width="50" height="50" aria-label="${`${
      count || 0
    } locations`}">
    ${(markers as Array<google.maps.marker.AdvancedMarkerElement>)
      ?.map((m) =>
        (m.content as HTMLElement).getAttribute("data-location-slug")
      )
      .filter(isTruthy)
      .map(
        (locationSlug) =>
          `<desc data-location-slug="${encodeNonAsciiHTML(locationSlug)}" />`
      )}
    <circle cx="120" cy="120" opacity=".6" r="${Math.min(
      60 + count * 5,
      120
    )}" />
    <text x="50%" y="54%" style="fill:#fff; font-weight: 800; font-family: ${TRADE_GOTHIC_NEXT_FONT_FAMILY}" font-size="80" text-anchor="middle" dominant-baseline="middle">${count}</text>
  </svg>`;

    const content = parser.parseFromString(
      svg,
      "image/svg+xml"
    ).documentElement;

    return new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      content,
    });
  }
}

export const sortByBucketKey = (
  a: Components.Schemas.UsedAssetAgg["buckets"][number],
  b: Components.Schemas.UsedAssetAgg["buckets"][number]
) => (a.key < b.key ? -1 : 1);

export const sortByBucketCount = (
  a: Components.Schemas.UsedAssetAgg["buckets"][number],
  b: Components.Schemas.UsedAssetAgg["buckets"][number]
) => (a.count < b.count ? 1 : -1);
